import { Component } from '@theme/component';
import { morph } from '@theme/morph';

class AlsoWearingComponent extends Component {
  #abortController = null;
  #cachedContent = new Map();

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#handleItemClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.removeEventListener('click', this.#handleItemClick);
  }

  #handleItemClick = async (event) => {
    const button = event.target.closest('[data-also-wearing-item]');
    if (!(button instanceof HTMLButtonElement)) return;

    event.preventDefault();

    const productUrl = button.dataset.productUrl;
    const variantId = button.dataset.variantId || '';
    if (!productUrl) return;

    const fullUrl = new URL(productUrl, window.location.origin);
    if (variantId) fullUrl.searchParams.set('variant', variantId);

    const cacheKey = fullUrl.toString();

    let productGrid = this.#cachedContent.get(cacheKey);
    if (!productGrid) {
      const html = await this.#fetchProductPage(cacheKey);
      if (!html) return;

      const gridElement = html.querySelector('[data-product-grid-content]');
      if (!gridElement) return;

      productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
      this.#cachedContent.set(cacheKey, productGrid);
    }

    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent || !productGrid) return;

    const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
    morph(modalContent, freshContent);

    this.#syncVariantSelection(modalContent, variantId);
    this.#injectModalTitle(modalContent, fullUrl.toString());
    this.#openModal();
  };

  async #fetchProductPage(url) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(url, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) return null;

      const responseText = await response.text();
      return new DOMParser().parseFromString(responseText, 'text/html');
    } catch (error) {
      if (error?.name === 'AbortError') return null;
      throw error;
    } finally {
      this.#abortController = null;
    }
  }

  #syncVariantSelection(modalContent, variantId) {
    if (!variantId) return;

    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');

    for (const input of modalInputs) {
      if (!(input instanceof HTMLInputElement)) continue;
      if (input.dataset.variantId !== variantId || input.checked) continue;

      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      break;
    }
  }

  #openModal() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!dialogComponent) return;

    const dialog = dialogComponent.querySelector('.quick-add-modal');
    if (!(dialog instanceof HTMLDialogElement)) return;

    dialog.setAttribute('data-also-wearing', 'true');
    dialog.addEventListener(
      'close',
      () => {
        dialog.removeAttribute('data-also-wearing');
      },
      { once: true }
    );

    if (typeof dialogComponent.showDialog === 'function') {
      dialogComponent.showDialog();
      requestAnimationFrame(() => {
        const content = dialog.querySelector('.quick-add-modal__content');
        if (content instanceof HTMLElement) {
          content.scrollTop = 0;
        }
      });
    }
  }

  #injectModalTitle(modalContent, productUrl = '') {
    const existingHeader = modalContent.querySelector('.also-wearing-modal__header');
    if (existingHeader instanceof HTMLElement) {
      existingHeader.remove();
    }

    const titleSource = modalContent.querySelector(
      '.product-details h1, .product-details .text-block.h3 > *, .product-details .view-product-title'
    );

    const titleText = titleSource?.textContent?.trim();
    if (!titleText) return;

    const titleWrap = document.createElement('div');
    titleWrap.className = 'also-wearing-modal__header';

    const title = document.createElement('h2');
    title.className = 'also-wearing-modal__title';

    if (productUrl) {
      const titleLink = document.createElement('a');
      titleLink.className = 'also-wearing-modal__title-link';
      titleLink.href = productUrl;
      titleLink.textContent = titleText;
      title.appendChild(titleLink);
    } else {
      title.textContent = titleText;
    }

    titleWrap.appendChild(title);
    modalContent.prepend(titleWrap);
  }
}

if (!customElements.get('also-wearing-component')) {
  customElements.define('also-wearing-component', AlsoWearingComponent);
}
